import React from 'react';
import { Redirect } from 'react-router-dom';
import classnames from 'classnames';
import { cloneDeep, has, filter, concat, pick, keys, values, isEqual, isEmpty } from 'lodash';

import UserConfirmation from '../route_utils/UserConfirmation';
import Tooltip from '../elements/mui/Tooltip';

import FileField from '../elements/FileField';
import Form from '../elements/forms/Form';
import FormPage from '../elements/forms/FormPage';
import PopUpForm from '../elements/pop-up-form/PopUpForm';
import TextEditable from '../elements/editors/mui/TextEditable';
import DebouncedText from '../elements/editors/mui/DebouncedText';
import {
  SelectInput,
  SelectOption,
  getCurrentSelectedOption,
  baseSelectStyles,
} from '../elements/rselect/ReactSelectUtils';
import SortedSelect from '../elements/rselect/SortedSelect';
import UnsortedSelect from '../elements/rselect/UnsortedSelect';
import { dataMapFields, getDataMapFromCSV } from '../../lib/DataMapsProcessor';
import {
  DATA_MAP_RECORDS_PER_PAGE,
  INSERT_TYPE_TABLE,
  INSERT_TYPE_BULK_INSERT,
  INSERT_TYPE_FILE_UPLOAD,
  WRITE_MODE_APPEND,
  WRITE_MODE_OVERWRITE,
} from '../../constants/dataMapConstants';
import { FORM_TYPE_EDIT_DATA_MAP } from '../../constants/formTypes';
import { ROUTE_DATA_MAPS } from '../../constants/pathnames';
import { SUPPORT_KEY_LOOKUP } from '../../constants/supportPageKeys';

import { ACLQueryModeTypes } from '../../constants/visibilityFilters';
import {
  Grid,
  CircularProgress,
  LinearProgress,
  InputBase,
  styled,
  inputBaseClasses,
} from '@mui/material';

import {
  Add,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon,
  VpnKey,
  AddCircleOutline,
  SkipPrevious as FirstPageIcon,
  ChevronLeft as PrevPageIcon,
  ChevronRight as NextPageIcon,
  SkipNext as LastPageIcon,
} from '@mui/icons-material';
import RoutePrefixContext from '../../../new/contexts/RoutePrefixContext';

import DataMapStyles from './DataMapsEditForm.module.scss';

const styles = {
  cards: {
    header: {
      paddingBottom: 0,
    },
    text: {
      backgroundColor: 'white',
      fontSize: 'inherit',
      padding: 16,
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  errorText: {
    fontSize: 11,
    color: 'red',
  },
  nameTextField: {
    root: {
      width: '100%',
      height: 32,
      fontSize: 12,
    },
  },
  descriptionTextField: {
    root: {
      width: '100%',
      height: 32,
      fontSize: 12,
      fontWeight: 400,
    },
  },
  addRowIcon: {
    opacity: 1,
    fontWeight: 500,
    margin: '0px 11px 0px 13px',
    color: '#5FC094',
    height: '14.3px',
    width: '14.3px',
    paddingTop: '-4px',
  },
  VirtualTextArea: {
    fontSize: 12,
    fontWeight: '400',
    width: '100%',
    color: 'rgba(39,52,56,0.7)',
    backgroundColor: '#FFFFFF',
    border: '1px solid #bbb',
    paddingLeft: 13,
    resize: 'vertical',
  },
  deleteIcon: {
    display: 'inline-block',
    margin: 'auto',
    // marginTop: '20px',
    cursor: 'pointer',
    color: '#C44A59',
  },
  sampleCellTextField: {
    col: {
      height: 32,
      display: 'flex',
      alignItems: 'center',
      paddingRight: 12,
      paddingLeft: 4,
    },
    deleteCol: {
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },

  tableCellTextField: {
    col: {
      height: 32,
      display: 'flex',
      alignItems: 'center',
    },
    deleteCol: {
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  editIcon: { cursor: 'pointer', color: '#596E84' },
  editIconDisabled: { cursor: 'not-allowed', color: '#596E84' },
  deleteARow: {
    display: 'inline-block',
    margin: 'auto',
    cursor: 'pointer',
    color: '#C44A59',
  },
  sampleValue: {
    overflow: 'auto',
    width: '100%',
    height: '100%',
    lineHeight: '32px',
    whiteSpace: 'nowrap',
  },
};

const muiStyles = {
  paginationInputBase: {
    padding: 5,
    [`.${inputBaseClasses.input}`]: {
      textAlign: 'right',
      width: 30,
      fontSize: 12,
      padding: 0,
    },
  },
  tableCellInputBase: {
    marginRight: 12,
    marginLeft: 4,
    [`.${inputBaseClasses.input}`]: {
      width: '100%',
      color: '#283443',
      fontSize: 12,
      paddingRight: 4,
      paddingLeft: 4,
      border: '1px solid #DFE8F0',
      borderRadius: '2px',
      backgroundColor: '#fff',
    },
  },
};

const StyledTableCellField = styled(InputBase)(muiStyles.tableCellInputBase);

const StyledPaginationField = styled(InputBase)(muiStyles.paginationInputBase);

class DataMapsEditForm extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);

    this.state = {
      clickX: -1,
      clickY: -1,
      awaitingSaveDataMap: false,
      requestedInsertType: null,
      showOverwriteWarning: false,
      redirectPath: null,
    };

    this.renderInsertModeContent = this.renderInsertModeContent.bind(this);
    this.renderDefaultsFields = this.renderDefaultsFields.bind(this);
    this.renderPaginationControls = this.renderPaginationControls.bind(this);
    this.renderTable = this.renderTable.bind(this);
    this.renderDataMapRows = this.renderDataMapRows.bind(this);
    this.renderWriteModeSelector = this.renderWriteModeSelector.bind(this);
    this.initFromDataMap = this.initFromDataMap.bind(this);
    this.onFormDismissed = this.onFormDismissed.bind(this);
    this.validateDataMap = this.validateDataMap.bind(this);
    this.adjustTableWidth = this.adjustTableWidth.bind(this);
    this.columnHeaderGenerator = this.columnHeaderGenerator.bind(this);
    this.onSubmitPropertyForm = this.onSubmitPropertyForm.bind(this);
    this.deleteKey = this.deleteKey.bind(this);
    this.onInsertTypeChanged = this.onInsertTypeChanged.bind(this);
    this.onWriteModeChanged = this.onWriteModeChanged.bind(this);
    this.onDiscardChangesResponse = this.onDiscardChangesResponse.bind(this);
    this.onOverwriteRecordsResponse = this.onOverwriteRecordsResponse.bind(this);
    this.onFileSelected = this.onFileSelected.bind(this);
    this.isDataMapCorrupted = this.isDataMapCorrupted.bind(this);
  }

  componentDidMount() {
    if (
      (this.props.dataMapIdParam &&
        this.props.dataMap &&
        // if expanded data map has been loaded, "data_map" must be set
        this.props.dataMap.data_map) ||
      !this.props.dataMapIdParam
    ) {
      this.initFromDataMap();
    } else if (this.props.dataMapIdParam) {
      this.props.loadDataMap(this.props.dataMapIdParam, {
        aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        expand: true,
      });
    }

    if (this.props.dataMapIdParam) {
      this.props.clearDataMapSearchResults();
      this.props.loadDataMapSamples(this.props.dataMapIdParam, 1, DATA_MAP_RECORDS_PER_PAGE);
    }

    this.adjustTableWidth();
    window.addEventListener('resize', this.adjustTableWidth);
    this.props.setContextualSupport(SUPPORT_KEY_LOOKUP);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.adjustTableWidth);
    this.props.unsetContextualSupport(SUPPORT_KEY_LOOKUP);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(prevProps.dynamicDataMapRows, this.props.dynamicDataMapRows)) {
      this.props.setDynamicDataMapRows(this.props.dynamicDataMapRows);
    }

    if (this.props.dataMapIdParam) {
      if (prevProps.keysPage !== this.props.keysPage) {
        this.props.loadDataMapSamples(
          this.props.dataMapIdParam,
          this.props.keysPage,
          DATA_MAP_RECORDS_PER_PAGE,
        );
      }

      if (prevProps.keysSearch !== this.props.keysSearch && this.props.keysSearch) {
        this.props.loadDataMapEntry(this.props.dataMapIdParam, this.props.keysSearch);
      }

      if (
        !prevProps.arePropertiesChanged &&
        this.props.arePropertiesChanged &&
        prevProps.writeMode === WRITE_MODE_APPEND &&
        this.props.writeMode === WRITE_MODE_OVERWRITE
      ) {
        this.setState({ showOverwriteWarning: true });
      }
    }

    if (this.state.awaitingSaveDataMap && !prevProps.saveComplete && this.props.saveComplete) {
      this.boundValidateDataMap();
    }

    if (prevProps.dataMapObj.map_entry_schema !== this.props.dataMapObj.map_entry_schema) {
      this.adjustTableWidth();
    }

    if (
      // if expanded data map has been loaded, "data_map" must be set
      (!prevProps.dataMap || !prevProps.dataMap.data_map) &&
      this.props.dataMap &&
      this.props.dataMap.data_map
    ) {
      this.initFromDataMap();
    }
  }

  initFromDataMap() {
    const { dataMap } = this.props;
    let dataMapObj;
    let isTypeStatic = true;

    if (!dataMap) {
      // new DM Create
      dataMapObj = {};
    } else {
      dataMapObj = { ...dataMap };
      if (dataMap.data_set_id) {
        isTypeStatic = false;
      }
    }

    this.props.clearStateToInitial();
    this.props.setInitialStateDataMapEditor(dataMapObj, this.props.dataMapIdParam, isTypeStatic);
  }

  onFormDismissed() {
    this.setState({ redirectPath: ROUTE_DATA_MAPS });
  }

  validateDataMap(data, onSuccess, onError) {
    let {
      dataMap,
      newRows,
      editedRows,
      deletedRows,
      bulkText,
      isTypeStatic,
      saveComplete,
      saveError,
      insertType,
      writeMode,
    } = this.props;
    let { awaitingSaveDataMap } = this.state;

    if (awaitingSaveDataMap && saveComplete) {
      this.setState({ awaitingSaveDataMap: false });

      if (saveError) {
        onError(saveError);
        return;
      }

      onSuccess();
      return;
    }

    this.boundValidateDataMap = this.validateDataMap.bind(this, ...arguments);

    let dataMapObj = cloneDeep(this.props.dataMapObj); // since dataMapObj was getting updating without dispatching an action
    let primaryKey = dataMapObj.map_primary_key;

    if (!primaryKey) {
      onError('The lookup cannot be saved without a primary key.');
      return;
    }

    if (!dataMapObj.name) {
      onError('The lookup cannot be saved without a name.');
      return;
    }

    if (isTypeStatic) {
      let data_map = dataMapObj.data_map;

      if (!dataMapObj.properties || dataMapObj.properties.length < 2) {
        onError('The lookup cannot have fewer than two columns.');
        return;
      }

      let numLinesInBulkText = 0;

      if (insertType === INSERT_TYPE_BULK_INSERT && bulkText) {
        const testText = bulkText.trim();

        for (
          let lineBreakIndex = 0;
          lineBreakIndex > -1;
          lineBreakIndex = testText.indexOf('\n', lineBreakIndex + 1)
        ) {
          numLinesInBulkText++;
        }
      }

      const isOverwriteMode = !dataMap || writeMode === WRITE_MODE_OVERWRITE;

      if (
        (insertType === INSERT_TYPE_TABLE && !dataMap && isEmpty(newRows)) ||
        (insertType === INSERT_TYPE_FILE_UPLOAD && isOverwriteMode && isEmpty(data_map)) ||
        (insertType === INSERT_TYPE_BULK_INSERT && isOverwriteMode && numLinesInBulkText < 2)
      ) {
        onError('The lookup cannot be saved without any records.');
        return;
      }

      const MAX_BULK_TEXT_LINES = 20000;

      if (
        numLinesInBulkText > MAX_BULK_TEXT_LINES ||
        (insertType === INSERT_TYPE_FILE_UPLOAD &&
          data_map &&
          data_map.length > MAX_BULK_TEXT_LINES)
      ) {
        onError(
          `Static lookups with more than ${MAX_BULK_TEXT_LINES} records are not supported. Consider creating a dynamic lookup from a Nexset or contact support.`,
        );
        return;
      }

      for (let i = dataMapObj.properties.length - 1; i > -1; i--) {
        if (!dataMapObj.properties[i]) {
          onError('Every column must have a name.');
          return;
        }
      }

      if (insertType === INSERT_TYPE_TABLE) {
        const updatedRows = filter(
          concat(values(editedRows), newRows),
          (item) => !has(deletedRows, item[primaryKey]),
        );

        let primeKeyNamesArray = [];

        for (let item of updatedRows) {
          // unique primary key name validation
          if (primeKeyNamesArray.indexOf(item[primaryKey]) > -1) {
            onError('The lookup cannot be saved with duplicate primary keys');
            return;
          }

          primeKeyNamesArray.push(item[primaryKey]);
        }
      }
    }

    let newFields = dataMapFields.filter((field) => {
      return field !== 'data_set_id' && field !== 'data_sink_id' && field !== 'map_entry_schema';
    });

    let data_map_info = pick(dataMapObj, newFields);
    let dataMapId = data_map_info.id;

    if (!dataMapId) {
      data_map_info.data_type = 'string';
    } else if (!isTypeStatic) {
      delete data_map_info.data_map; // only the properties name, description, Nexset id, map_primary_key, and data_defaults remain
    }

    this.setState({ awaitingSaveDataMap: true }, () => {
      this.props.saveDataMapFromForm(data_map_info);
    });
  }

  onSubmitPropertyForm() {
    let { propBeingEdited, dataMapObj, buttonLabel } = this.props;
    let propertiesObj = dataMapObj.map_entry_schema && dataMapObj.map_entry_schema.properties;
    let { map_primary_key } = dataMapObj;

    if (
      (has(propertiesObj, propBeingEdited.name) || propBeingEdited.name === '') &&
      buttonLabel === 'Add'
    ) {
      // unique name validation for newly added keys
      this.props.setError({
        type: 'SET_PROPERTY_KEY_DATA_MAPS',
        msg: 'Key name must be unique',
        error: { msg: 'abc' },
      });
    } else if (!this.props.fields[2].value && propBeingEdited.name === map_primary_key) {
      // check if unchecked and isPrimary
      this.props.setError({
        type: 'REMOVE_PRIMARY_KEY_PROPERTY_KEY_DATA_MAPS',
        msg: 'Cannot remove a primary key without replacing it',
        error: { msg: 'abc' },
      });
    } else {
      this.props.onSubmitPropertyForm();
    }
  }

  deleteKey() {
    if (this.props.propBeingEdited.name === this.props.dataMapObj.map_primary_key) {
      this.props.setError({
        type: 'DELETE_DATA_MAP_KEY',
        msg: 'cannot delete a primary key',
      });
    } else {
      this.props.deleteKey();
    }
  }

  onInsertTypeChanged(event) {
    const {
      insertType,
      newRows,
      editedRows,
      deletedRows,
      bulkText,
      isFileSelected,
      arePropertiesChanged,
    } = this.props;
    const { insertType: newInsertType } = event.currentTarget.dataset;

    if (newInsertType === insertType) {
      return;
    }

    // check if there are unsaved changes
    if (
      arePropertiesChanged ||
      (insertType === INSERT_TYPE_TABLE &&
        (!isEmpty(newRows) || !isEmpty(editedRows) || !isEmpty(deletedRows))) ||
      (insertType === INSERT_TYPE_BULK_INSERT &&
        bulkText &&
        bulkText.indexOf('\n') > -1 &&
        bulkText.length > bulkText.indexOf('\n') + 1) ||
      (insertType === INSERT_TYPE_FILE_UPLOAD && isFileSelected)
    ) {
      this.setState({ requestedInsertType: newInsertType });
    } else {
      this.setState({ requestedInsertType: null });
      this.props.setInsertType(newInsertType);
    }
  }

  onWriteModeChanged(newWriteMode) {
    const { writeMode } = this.props;

    if (newWriteMode === writeMode) {
      return;
    }

    this.props.setWriteMode(newWriteMode);
  }

  onDiscardChangesResponse(response) {
    if (response) {
      this.props.setInsertType(this.state.requestedInsertType);
    }

    this.setState({ requestedInsertType: null });
  }

  onOverwriteRecordsResponse() {
    this.setState({ showOverwriteWarning: false });
  }

  onFileSelected(content) {
    const { setError, onChangeSelectedFile } = this.props;

    if (!content) {
      onChangeSelectedFile(null);
      return;
    }

    getDataMapFromCSV(content)
      .then((dataMapRows) => {
        onChangeSelectedFile(content, dataMapRows);
      })
      .catch((error) => {
        onChangeSelectedFile(null);
        setError({
          type: 'INVALID_CSV',
          msg: 'Please provide valid CSV file.',
        });
      });
  }

  isDataMapCorrupted() {
    const { dataMapObj, dataMapSamples, isTypeStatic, isLoadingSamples } = this.props;
    const { map_entry_schema } = dataMapObj;
    const propKeysArray = keys(map_entry_schema && map_entry_schema.properties);

    if (!isLoadingSamples && !isTypeStatic && dataMapSamples.length > 0) {
      const dataMapKeys = keys(dataMapSamples[0]);
      if (
        propKeysArray.indexOf(dataMapKeys[0]) === -1 ||
        propKeysArray.indexOf(dataMapKeys[1]) === -1
      ) {
        return true;
      }
    }

    return false;
  }

  columnHeaderGenerator(property, idx, isPrimary) {
    const {
      dataMapIdParam,
      propBeingEditedFormerName,
      isKeyFormOpen,
      buttonLabel,
      readOnly,
      insertType,
    } = this.props;

    const isEditMode = !!dataMapIdParam;
    const isColumnEditMode =
      buttonLabel === 'Save' && isKeyFormOpen && propBeingEditedFormerName === property.name;
    const isTableMode = insertType === INSERT_TYPE_TABLE;

    if (idx === -1) {
      idx = '00-01-02';
    }

    return (
      <Grid
        item
        xs
        className={classnames('column-header', {
          'edit-mode': isColumnEditMode,
        })}
        key={idx}
      >
        <div className="column-header-title-container">
          <div className="column-header-title" title={property.name}>
            {property.name}
            {isPrimary && (
              <VpnKey
                fontSize="small"
                style={{ paddingLeft: 'var(--spacing-xs)', color: 'white' }}
              />
            )}
          </div>
          {isTableMode && this.props.isTypeStatic && !readOnly && (
            <Tooltip
              title={
                isEditMode
                  ? 'Modifying columns after saving the lookup is not supported. Please contact support to edit the columns for you.'
                  : 'Edit Column'
              }
              arrow={true}
            >
              <EditIcon
                fontSize="large"
                style={isEditMode ? styles.editIconDisabled : styles.editIcon}
                onClick={
                  isEditMode
                    ? null
                    : (e) => {
                        this.setState(
                          {
                            clickX: e.clientX,
                            clickY: e.clientY,
                          },
                          () => {
                            this.props.toggleKeyForm(
                              'Edit Property',
                              'Save',
                              property.id,
                              property,
                            );
                          },
                        );
                      }
                }
              />
            </Tooltip>
          )}
        </div>
        {isTableMode && (
          <div className="property-type">
            {property.type}
            <span className="separator"> | </span>
            Default: {property.default}
          </div>
        )}
      </Grid>
    );
  }

  renderInsertModeContent() {
    let { dataMap, dataMapIdParam, insertType, isTypeStatic, isFileSelected } = this.props;
    let isEditMode = !!dataMapIdParam;

    switch (insertType) {
      case INSERT_TYPE_BULK_INSERT:
        return (
          <div>
            <div className="tab-inner-section">
              <div
                style={{
                  padding: 'var(--spacing-lg)',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: 15,
                  ...styles.cards.header,
                }}
              >
                Enter Data
              </div>
              <div style={styles.cards.text}>
                <div className="virtual-text-area-container">
                  <div className="field-label">
                    Values separated by commas and delimited by line breaks. The first line contains
                    column names and each of the following lines defines a record in the lookup.
                  </div>
                  <textarea
                    autoComplete="off"
                    value={this.props.bulkText}
                    onChange={(e) => {
                      this.props.onChangeBulkInsert(e.target.value);
                    }}
                    spellCheck="false"
                    rows={12}
                    style={styles.VirtualTextArea}
                  />
                </div>
                {isEditMode && this.renderWriteModeSelector()}
              </div>
            </div>
            {this.renderDefaultsFields()}
          </div>
        );

      case INSERT_TYPE_FILE_UPLOAD:
        return (
          <div>
            <div className="tab-inner-section">
              <div
                style={{
                  padding: 'var(--spacing-lg)',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: 15,
                  ...styles.cards.header,
                }}
              >
                Choose File
              </div>
              <div style={styles.cards.text}>
                <div className="field-label">
                  Select a CSV file containing values separated by commas and delimited by line
                  breaks. The first line contains column names and each of the following lines
                  defines a record in the lookup.
                </div>
                <FileField
                  buttonType="secondary"
                  buttonStyle={{ width: '100%' }}
                  onChange={this.onFileSelected}
                />

                {isEditMode && isFileSelected && this.renderWriteModeSelector()}
              </div>
            </div>
            {isFileSelected && (
              <div className="tab-inner-section">
                <div
                  style={{
                    padding: 'var(--spacing-lg)',
                    fontWeight: 'var(--font-weight-medium)',
                    fontSize: 15,
                    ...styles.cards.header,
                  }}
                >
                  File Preview
                </div>
                <div style={styles.cards.text}>{this.renderTable()}</div>
              </div>
            )}
            {isFileSelected && this.renderDefaultsFields()}
          </div>
        );

      default:
        if (isTypeStatic) {
          return (
            <div>
              {dataMap && this.renderPaginationControls()}
              {this.renderTable()}
            </div>
          );
        }

        return (
          <div>
            <div className="tab-inner-section">
              <div
                aria-label="lookup-preview-title"
                style={{
                  padding: 'var(--spacing-lg)',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: 15,
                  ...styles.cards.header,
                }}
              >
                Lookup Preview
              </div>
              <div style={styles.cards.text}>
                {dataMap && this.renderPaginationControls()}
                {this.renderTable()}
              </div>
            </div>
            {this.renderDefaultsFields()}
          </div>
        );
    }
  }

  renderDefaultsFields() {
    let { dataMapObj } = this.props;
    let { map_primary_key, map_entry_schema, properties } = dataMapObj;
    let propertiesObj = map_entry_schema && map_entry_schema.properties;
    let propertiesArray = properties || [];
    let primaryProp;

    if (propertiesObj) {
      primaryProp = propertiesObj[map_primary_key];
      if (!primaryProp) {
        primaryProp = propertiesObj[this.props.propBeingEditedFormerName];
      }
    }

    let primaryKeyOptions = propertiesArray.map((propertyName) => ({
      label: propertyName,
      value: propertyName,
    }));
    let selectedPrimaryKeyOption = getCurrentSelectedOption(primaryKeyOptions, map_primary_key);

    if (propertiesArray.length === 0) {
      return null;
    }

    return (
      <div className="tab-inner-section">
        <div
          style={{
            padding: 'var(--spacing-lg)',
            fontWeight: 'var(--font-weight-medium)',
            fontSize: 15,
            ...styles.cards.header,
          }}
        >
          Column Configuration
        </div>
        <div style={styles.cards.text}>
          <div>
            <div className="field-label">Choose the primary key column</div>
            <SortedSelect
              value={selectedPrimaryKeyOption}
              // styles={styles}
              options={primaryKeyOptions}
              components={{ Input: SelectInput, Option: SelectOption }}
              isClearable={false}
              backspaceRemovesValue={false}
              isSearchable={true}
              onChange={(option) => this.props.setPrimaryKey(option.value)}
            />
          </div>
          <div className="full-width-table-label field-label">Assign default values to columns</div>
          <div className="full-width-table">
            <div className="table-header">
              <Grid container className="table-header-row">
                <Grid item xs={5}>
                  Lookup Column Name
                </Grid>
                <Grid item xs={7}>
                  Default Value
                </Grid>
              </Grid>
            </div>
            <div className="table-body">
              {propertiesArray.map((propertyName, index) => (
                <Grid container key={'form-attributes-json' + index} className="table-body-row">
                  <Grid item xs={5}>
                    <div
                      style={{
                        fontSize: 'var(--font-size-base-sm)',
                        color: '#4E636A',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {propertyName}
                    </div>
                  </Grid>
                  <Grid item xs={7}>
                    <div
                      style={{
                        height: 32,
                        display: 'flex',
                        flexGrow: 1,
                        alignItems: 'center',
                        paddingRight: 12,
                        paddingLeft: 'var(--spacing-sm)',
                      }}
                    >
                      <TextEditable
                        className="dataMapSampleTextField"
                        id={'form-attributes-json-data_defaults.' + propertyName}
                        name={'form-text-data_defaults.' + propertyName}
                        rootStyle={{ width: '100%' }}
                        placeholder={`Default value for ${propertyName}`}
                        value={dataMapObj.data_defaults[propertyName] || ''}
                        onChange={(text) => {
                          this.props.setDefaultValue(propertyName, text);
                        }}
                      />
                    </div>
                  </Grid>
                </Grid>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderPaginationControls() {
    let {
      dataMapObj,
      dataMapSamples,
      dataMapSearchResults,
      insertType,
      keysPage,
      isLoadingSamples,
      isLoadingSearchResults,
    } = this.props;
    let { map_primary_key, map_entry_schema } = dataMapObj;

    let propertiesObj = map_entry_schema && map_entry_schema.properties;

    let primaryProp;

    if (propertiesObj) {
      primaryProp = propertiesObj[map_primary_key];
      if (!primaryProp) {
        primaryProp = propertiesObj[this.props.propBeingEditedFormerName];
      }
    }

    let rowsData = dataMapSearchResults || dataMapSamples;
    let shouldShowLinearLoading = isLoadingSamples || isLoadingSearchResults;
    let isPrevPageEnabled = keysPage > 1;
    let isNextPageEnabled = rowsData.length >= DATA_MAP_RECORDS_PER_PAGE;
    let isTableMode = insertType === INSERT_TYPE_TABLE;

    return (
      <div className="pagination-container">
        <DebouncedText
          className="filter-value-text-field"
          id="KeySearch"
          placeholder="Search by primary key"
          value={this.props.keysSearch}
          debounceTimeMs={400}
          rootStyle={{ width: 250, height: 28, backgroundColor: '#fff' }}
          type="search"
          onChange={this.props.onSetSearchTerm}
        />

        <span
          className="pagination-button"
          onClick={(e) => {
            this.props.onFirstPage();
          }}
        >
          <FirstPageIcon fontSize="large" />
        </span>
        <span
          className="pagination-button"
          onClick={(e) => {
            isPrevPageEnabled && this.props.onPrevPage();
          }}
        >
          <PrevPageIcon fontSize="large" color={isPrevPageEnabled ? 'inherit' : 'disabled'} />
        </span>
        <StyledPaginationField
          margin="dense"
          type="text"
          inputProps={{
            autoComplete: 'new-password',
          }}
          value={this.props.keysPage}
          onChange={(e) => {
            this.props.onSetPage(e.target.value);
          }}
        />

        <span className="total-page-count">
          of {this.props.keysTotalPage > 0 ? this.props.keysTotalPage : 1}
        </span>
        <span
          className="pagination-button"
          onClick={(e) => {
            isNextPageEnabled && this.props.onNextPage();
          }}
        >
          <NextPageIcon fontSize="large" color={isNextPageEnabled ? 'inherit' : 'disabled'} />
        </span>
        <span
          className="pagination-button"
          onClick={(e) => {
            this.props.onLastPage();
          }}
        >
          <LastPageIcon fontSize="large" />
        </span>
        {isTableMode && shouldShowLinearLoading && (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
            <LinearProgress
              variant="indeterminate"
              className="progress"
              style={{ marginBottom: 0 }}
            />
          </div>
        )}
      </div>
    );
  }

  renderTable() {
    let {
      dataMapObj,
      isKeyFormOpen,
      insertType,
      buttonLabel,
      fields,
      popUpFormTitle,
      propBeingEdited,
      readOnly,
      isTypeStatic,
      isFileSelected,
      isLoadingDataMap,
    } = this.props;
    let { clickX, clickY } = this.state;
    let { data_map, map_primary_key, map_entry_schema, properties } = dataMapObj;

    let propertiesObj = map_entry_schema && map_entry_schema.properties;
    let propertiesArray = properties || [];

    // .2 is for the 'add' a new property button's column

    let addIconColor = isKeyFormOpen && buttonLabel === 'Add' ? '#fff' : '#DFE8F0';
    let addIconBackGroundColor = isKeyFormOpen && buttonLabel === 'Add' ? '#4AD496' : '#1F2F40';

    let primaryProp;

    if (propertiesObj) {
      primaryProp = propertiesObj[map_primary_key];
      if (!primaryProp) {
        primaryProp = propertiesObj[this.props.propBeingEditedFormerName];
      }
    }

    let isEditable = !isLoadingDataMap && isTypeStatic && !readOnly;
    let isTableMode = insertType === INSERT_TYPE_TABLE;
    let isFileMode = insertType === INSERT_TYPE_FILE_UPLOAD;
    let fileRows = isFileMode && data_map ? data_map.slice(0, 5) : [];
    let totalFileRows = data_map ? data_map.length : 0;

    return (
      <div>
        {propBeingEdited && (
          <div className={DataMapStyles.popUpFormContainer}>
            <PopUpForm
              fields={fields}
              submitButtonLabel={buttonLabel}
              showRequiredIndicator={false}
              heading={popUpFormTitle}
              data={propBeingEdited}
              customButtons={
                buttonLabel === 'Save' && isTableMode
                  ? [
                      {
                        label: 'Delete',
                        buttonTheme: 'delete',
                        onClick: this.deleteKey,
                      },
                    ]
                  : null
              }
              onCloseClicked={this.props.closeKeyForm}
              onFieldUpdated={this.props.onFieldUpdated}
              onSubmit={this.onSubmitPropertyForm}
              isCredentialsForm={true}
              rootStyle={{
                left: Math.min(clickX, window.innerWidth - 460),
                top: clickY + 35,
                width: 300,
              }}
              fieldClassNames={['datamap-form']}
            />
          </div>
        )}
        {(isTableMode || (isFileMode && isFileSelected && !isEmpty(fileRows))) && (
          <div className="data-maps-table-container">
            <div className="data-maps-table">
              <Grid container className="table-col-headers-container">
                {primaryProp && this.columnHeaderGenerator(primaryProp, -1, true)}
                {propertiesArray.length > 0 &&
                  propertiesArray.map((itemName, idx) => {
                    if (
                      itemName !== map_primary_key &&
                      (!primaryProp || itemName !== primaryProp.name)
                    ) {
                      return this.columnHeaderGenerator(propertiesObj[itemName], idx, false);
                    } else {
                      return <span key={idx} />;
                    }
                  })}
                {isEditable && isTableMode && (
                  <Grid item xs={1}>
                    <div
                      style={{
                        backgroundColor: addIconBackGroundColor,
                        borderRight: '1px solid #52677C',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AddCircleOutline
                        fontSize="large"
                        style={{
                          width: '40px',
                          color: addIconColor,
                          cursor: 'pointer',
                        }}
                        onClick={(e) => {
                          this.setState(
                            {
                              clickX: e.clientX,
                              clickY: e.clientY,
                            },
                            () => {
                              this.props.toggleKeyForm('Add New Key', 'Add', -1, null);
                            },
                          );
                        }}
                      />
                    </div>
                  </Grid>
                )}
              </Grid>

              {isTableMode && this.renderDataMapRows()}

              {isFileMode && isFileSelected && !isEmpty(fileRows) && (
                <div>
                  {this.renderDataMapRows(fileRows)}
                  <div>
                    {fileRows.length} of {totalFileRows} records shown
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  renderDataMapRows(rows) {
    let {
      keysPage,
      editedRows,
      deletedRows,
      newRows,
      dataMapIdParam,
      dataMapObj,
      dataMapSamples,
      dataMapSearchResults,
      readOnly,
      isTypeStatic,
      insertType,
      isLoadingDataMap,
      isLoadingSamples,
      isLoadingSearchResults,
    } = this.props;
    let { map_entry_schema, map_primary_key, properties } = dataMapObj;

    let propertiesArray = properties || [];
    let primaryKeyIndex = propertiesArray.indexOf(map_primary_key);
    let sortedPropertiesArray = [...propertiesArray];

    if (primaryKeyIndex > -1) {
      sortedPropertiesArray.splice(primaryKeyIndex, 1);
      sortedPropertiesArray.unshift(map_primary_key);
    }

    let isEditMode = !!dataMapIdParam;
    let isFileMode = insertType === INSERT_TYPE_FILE_UPLOAD;
    let colCount = sortedPropertiesArray.length;
    let rowsData = rows || dataMapSearchResults || dataMapSamples;
    let renderReadOnlyRows = !isTypeStatic || readOnly || isFileMode;
    let showSpinner =
      isLoadingDataMap || ((isLoadingSamples || isLoadingSearchResults) && rowsData.length === 0);

    if (!renderReadOnlyRows) {
      colCount += 0.2;
    }

    return (
      <div>
        {isEditMode &&
          rowsData &&
          rowsData.length === 0 &&
          !isLoadingSearchResults &&
          !isLoadingSamples && <p className="no-data-message">No records found.</p>}
        {rowsData.length > 0 &&
          rowsData.map((rowSample, rowIndex) => {
            if (rowSample) {
              let rowKey = rowSample[map_primary_key];
              let isRowDeleted = has(deletedRows, rowKey);
              let isRowEdited = has(editedRows, rowKey);
              let rowData = isRowEdited ? editedRows[rowKey] : rowSample;

              return (
                <Grid
                  container
                  key={rowIndex}
                  className={classnames('table-rows-container', {
                    edited: isRowEdited,
                    deleted: isRowDeleted,
                  })}
                >
                  {sortedPropertiesArray.map((propItemName, colIdx) => (
                    <Grid item xs key={`${colIdx}-${rowIndex}-${keysPage}`}>
                      <div style={styles.sampleCellTextField.col}>
                        {!renderReadOnlyRows && (
                          <TextEditable
                            id={`${colIdx}-${rowIndex}-${propItemName}`}
                            className="dataMapSampleTextField"
                            key={`${colIdx}-${rowIndex}-${keysPage}`}
                            rootStyle={{ width: '100%' }}
                            value={rowData[propItemName]}
                            onChange={(value) => {
                              this.props.changeDataMapCell(propItemName, value, rowKey, rowData);
                            }}
                          />
                        )}
                        {renderReadOnlyRows && !isLoadingSamples && (
                          <div style={styles.sampleValue}>{`${rowSample[propItemName]}`}</div>
                        )}
                      </div>
                    </Grid>
                  ))}
                  {!renderReadOnlyRows && (
                    <Grid item xs={1} key={`${colCount}-${rowIndex}-${keysPage}`}>
                      <div style={styles.sampleCellTextField.deleteCol}>
                        {!renderReadOnlyRows &&
                          ((isRowDeleted && (
                            <Tooltip title="Undo Delete" arrow={true}>
                              <UndoIcon
                                fontSize="large"
                                style={styles.deleteARow}
                                onClick={() => {
                                  this.props.undoDeleteRow(rowKey);
                                }}
                              />
                            </Tooltip>
                          )) || (
                            <Tooltip title="Delete Row" arrow={true}>
                              <DeleteIcon
                                fontSize="large"
                                style={styles.deleteARow}
                                onClick={() => {
                                  this.props.deleteRow(rowKey);
                                }}
                              />
                            </Tooltip>
                          ))}
                      </div>
                    </Grid>
                  )}
                </Grid>
              );
            } else {
              return <span />;
            }
          })}
        {!renderReadOnlyRows && newRows.length > 0 && (
          <div>
            {newRows.map((item, rowIdx) => (
              <Grid
                container
                className="extra-row-for-adding extra_For_Adding_A_New_One"
                key={`00-${rowIdx}-${keysPage}`}
              >
                {sortedPropertiesArray.map((propItem, colIdx) => {
                  let cellValue = newRows[rowIdx][propItem] ? newRows[rowIdx][propItem] : '';
                  return (
                    <Grid item xs key={`${colIdx}-${propItem}`}>
                      <div style={styles.tableCellTextField.col}>
                        <StyledTableCellField
                          key={`${colIdx}-${rowIdx}-${keysPage}`}
                          inputProps={{
                            autoComplete: 'new-password',
                          }}
                          margin="dense"
                          value={cellValue}
                          fullWidth={true}
                          onChange={(e) => {
                            this.props.updateRowBeingAdded(propItem, rowIdx, e.target.value);
                          }}
                        />
                      </div>
                    </Grid>
                  );
                })}
                <Grid item xs={1} key={`999-${rowIdx}-${keysPage}`}>
                  <div style={styles.tableCellTextField.deleteCol}>
                    <Tooltip title="Delete Row" arrow={true}>
                      <DeleteIcon
                        fontSize="large"
                        style={styles.deleteIcon}
                        onClick={(e) => {
                          this.props.deleteNewRow(rowIdx);
                        }}
                      />
                    </Tooltip>
                  </div>
                </Grid>
              </Grid>
            ))}
          </div>
        )}
        {!showSpinner && !renderReadOnlyRows && map_entry_schema && (
          <div
            className="extra_For_Adding_A_New_One add-row-button"
            key={3}
            onClick={(e) => {
              this.props.addAnotherNewRow();
            }}
          >
            <Add fontSize="large" style={styles.addRowIcon} />
            <div className="add-row-text">Add new row</div>
          </div>
        )}
        {showSpinner && (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 'var(--spacing-lg)',
            }}
          >
            <CircularProgress color="primary" />
          </div>
        )}
      </div>
    );
  }

  renderWriteModeSelector() {
    let { writeMode, arePropertiesChanged } = this.props;
    let options = [
      {
        label: 'Append to existing records',
        value: WRITE_MODE_APPEND,
        isDisabled: arePropertiesChanged,
      },
      {
        label: 'Overwrite existing records',
        value: WRITE_MODE_OVERWRITE,
      },
    ];

    let selectedOption = getCurrentSelectedOption(options, writeMode);

    return (
      <div className="write-modes-container">
        <UnsortedSelect
          value={selectedOption}
          options={options}
          styles={{
            ...baseSelectStyles,
            container: (base) => ({
              ...base,
              width: 350,
              marginRight: 16,
            }),
          }}
          components={{ Input: SelectInput, Option: SelectOption }}
          isClearable={false}
          backspaceRemovesValue={false}
          isSearchable={true}
          onChange={(option) => this.onWriteModeChanged(option.value)}
        />

        {arePropertiesChanged &&
          'Appending is disabled because the new columns are different from the saved ones.'}
      </div>
    );
  }

  adjustTableWidth() {
    const { properties } = this.props.dataMapObj;
    const numProps = properties ? properties.length + 0.2 : 0; // .2 is to account for "Add" column

    if (numProps === 0) {
      return;
    }

    const table = document.querySelector('.data-maps-table');
    if (table) {
      let columnWidth = 0;
      columnWidth = table.offsetWidth / numProps;
      const minColumnWidth = 150;
      const newTableWidth =
        columnWidth < minColumnWidth ? minColumnWidth * numProps + 'px' : 'unset';
      table.style.width = newTableWidth;
    }
  }

  render() {
    if (this.state.redirectPath) {
      return <Redirect to={`${this.context}${this.state.redirectPath}`} />;
    }

    const {
      dataMapIdParam,
      dataMapObj,
      isTypeStatic,
      insertType,
      readOnly,
      owner,
      isLoadingDataMap,
    } = this.props;
    const { requestedInsertType, showOverwriteWarning } = this.state;
    const isCorrupted = this.isDataMapCorrupted();
    const isEditable = !isLoadingDataMap && isTypeStatic && !readOnly;
    const isEditMode = !!dataMapIdParam;

    const nameField = readOnly ? (
      <div style={styles.nameTextField.root}>{dataMapObj.name}</div>
    ) : (
      <TextEditable
        id="name"
        className="dataMapMetaTextField"
        key={-100}
        disabled={readOnly}
        rootStyle={{ width: '70%', height: 32, marginBottom: 6 }}
        placeholder={'Name your lookup...'}
        value={dataMapObj.name}
        onChange={(e) => {
          this.props.updateDataMapMeta('name', e);
        }}
      />
    );

    const descField = readOnly ? (
      <div style={styles.descriptionTextField.root}>{dataMapObj.description || ''}</div>
    ) : (
      <TextEditable
        id="description"
        className="dataMapMetaTextField"
        key={-101}
        placeholder={'Describe your lookup...'}
        disabled={readOnly}
        rootStyle={{ width: '70%', height: 32 }}
        value={dataMapObj.description}
        onChange={(e) => {
          this.props.updateDataMapMeta('description', e);
        }}
      />
    );

    return (
      <div>
        <Form
          title={isEditMode ? 'Edit Lookup' : 'Create Lookup'}
          type={FORM_TYPE_EDIT_DATA_MAP}
          subtitle={dataMapObj.name}
          onSubmit={this.onFormDismissed}
          onDismiss={this.onFormDismissed}
          alwaysShowCancel={true}
          submitButtonText="Save"
        >
          <FormPage
            title=""
            validationMethod={this.validateDataMap}
            nextButtonText="Save"
            contentHeight={800}
          >
            <div className="data-maps-edit-form">
              <div
                className="form-section meta-data-container"
                style={{ backgroundColor: 'white', boxShadow: 'none' }}
              >
                <div style={{ ...styles.cards.text, padding: 'var(--spacing-lg)' }}>
                  {readOnly && <div className="read-only-form-label">Collaborator</div>}
                  <div className="field-label"> Name</div>
                  <div>{nameField}</div>

                  <div className="field-label"> Description</div>
                  <div>{descField}</div>
                  {readOnly && owner && (
                    <div className="field-label">
                      <b>Owner</b> {owner.email}
                    </div>
                  )}
                </div>
              </div>
              {!isLoadingDataMap && (
                <div className="form-section" style={{ boxShadow: 'none' }}>
                  {isEditable && (
                    <div className="form-section-tabs-container">
                      <div
                        data-insert-type={INSERT_TYPE_TABLE}
                        className={classnames('form-section-tab', {
                          selected: insertType === INSERT_TYPE_TABLE,
                        })}
                        onClick={this.onInsertTypeChanged}
                      >
                        <h1>Enter Data in Table</h1>
                        <div className="mode-subtitle">
                          Select this mode if you want to create, modify, or delete records in an
                          interactive table.
                        </div>
                      </div>
                      <div
                        data-insert-type={INSERT_TYPE_BULK_INSERT}
                        className={classnames('form-section-tab', {
                          selected: insertType === INSERT_TYPE_BULK_INSERT,
                        })}
                        onClick={this.onInsertTypeChanged}
                      >
                        <h1 className="mode-title">Enter Data as Raw Text</h1>
                        <div className="mode-subtitle">
                          Select this mode if you want to enter your data as text.
                          {isEditMode &&
                            ' You can either add new records to the lookup or overwrite all existing ones.'}
                        </div>
                      </div>
                      <div
                        data-insert-type={INSERT_TYPE_FILE_UPLOAD}
                        className={classnames('form-section-tab', {
                          selected: insertType === INSERT_TYPE_FILE_UPLOAD,
                        })}
                        onClick={this.onInsertTypeChanged}
                      >
                        <h1 className="mode-title">Load Data from File</h1>
                        <div className="mode-subtitle">
                          Select this mode if you want to import data from a local file.
                          {isEditMode &&
                            ' You can either add new records to the lookup or overwrite all existing ones.'}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="form-section-tab-content" style={styles.cards.text}>
                    {isCorrupted ? (
                      <p className="errorText">This lookup is corrupted.</p>
                    ) : (
                      this.renderInsertModeContent()
                    )}
                  </div>
                </div>
              )}
            </div>
          </FormPage>
        </Form>
        {requestedInsertType && (
          <UserConfirmation
            message="Switching to a different input mode will cause you to lose all unsaved changes. Do you want to continue?"
            cancelButtonLabel="Cancel"
            okButtonLabel="Continue"
            onCancel={() => this.onDiscardChangesResponse(false)}
            onConfirm={() => this.onDiscardChangesResponse(true)}
          />
        )}
        {showOverwriteWarning && (
          <UserConfirmation
            title="Overwrite Warning"
            message="The new records cannot be appended to the existing lookup records because the columns have changed. All existing records will be overwritten."
            okButtonLabel="OK"
            showCancelButton={false}
            onConfirm={this.onOverwriteRecordsResponse}
          />
        )}
      </div>
    );
  }
}
export default DataMapsEditForm;
